import React, { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import { Th, Td } from '../WMSShared';
import axios from 'axios';

export default function CrudModal({
  modal, editItem, form, F, setForm, close, warehouses, products, customers, suppliers,
  orderItems, setOrderItems, purchaseItems, setPurchaseItems,
  orderTotal, purchaseTotal, saveProduct, saveOrder, saveWarehouse,
  saveCustomer, saveUser, saveSupplier, savePurchase,
  wmsConfig = {}, exchangeRates = {}
}) {
  const cur      = wmsConfig.currency_symbol || 'S$';
  const baseCode = wmsConfig.currency_code   || 'SGD';
  const taxRates = wmsConfig.tax_rates || [{ id: 'none', name: 'No Tax', rate: 0 }, { id: 'gst9', name: 'GST 9% (SG)', rate: 9 }];
  const fxRates  = exchangeRates.rates || {};   // rates relative to USD

  const API = process.env.REACT_APP_BACKEND_URL + '/api/wms';
  const [supplierProducts, setSupplierProducts] = useState([]);
  const [allSupplierProducts, setAllSupplierProducts] = useState([]);

  // Load ALL supplier-product mappings once when purchase modal opens
  useEffect(() => {
    if (modal === 'purchase') {
      axios.get(`${API}/supplier-products-all`).then(r => setAllSupplierProducts(r.data)).catch(() => setAllSupplierProducts([]));
    }
  }, [modal]);

  // When supplier changes, filter mappings for that supplier
  const selectedSupplier = suppliers.find(s => s.name === form.supplier);
  useEffect(() => {
    if (selectedSupplier) {
      setSupplierProducts(allSupplierProducts.filter(sp => sp.supplier_id === selectedSupplier.supplier_id));
    } else {
      setSupplierProducts([]);
    }
  }, [selectedSupplier?.supplier_id, allSupplierProducts]);

  // Given the products already added, which suppliers carry ALL of them?
  const addedProductIds = (purchaseItems || []).map(i => i.product_id);
  const compatibleSupplierIds = addedProductIds.length > 0
    ? suppliers.filter(s => {
        const supplierMappedIds = new Set(allSupplierProducts.filter(sp => sp.supplier_id === s.supplier_id).map(sp => sp.product_id));
        return addedProductIds.every(pid => supplierMappedIds.has(pid));
      }).map(s => s.supplier_id)
    : suppliers.map(s => s.supplier_id);
  const compatibleSupplierSet = new Set(compatibleSupplierIds);

  // Products to show: if supplier selected, only their products.
  // If no supplier, only products that share at least one common supplier with ALL already-added products.
  const mappedProductIds = new Set(supplierProducts.map(sp => sp.product_id));

  const availableProducts = (() => {
    if (selectedSupplier && supplierProducts.length > 0) {
      return products.filter(p => mappedProductIds.has(p.product_id));
    }
    if (addedProductIds.length === 0) {
      return products;
    }
    // Find products that share at least one common supplier with all added products
    // i.e., there exists at least one supplier that carries this product AND all added products
    return products.filter(p => {
      const pSuppliers = new Set(allSupplierProducts.filter(sp => sp.product_id === p.product_id).map(sp => sp.supplier_id));
      // Check if any supplier carries both this product AND all added products
      return [...pSuppliers].some(sid => compatibleSupplierSet.has(sid));
    });
  })();
  const fxBase   = exchangeRates.base  || 'USD';
  const fxSource = exchangeRates.source || '';

  // Convert amount FROM foreignCode TO baseCode
  const toBase = (amount, foreignCode) => {
    if (!foreignCode || foreignCode === baseCode) return amount;
    if (!fxRates[foreignCode] || !fxRates[baseCode]) return amount;
    // amount in foreignCode → USD → baseCode
    const inUsd = amount / fxRates[foreignCode];
    return inUsd * fxRates[baseCode];
  };

  // Exchange rate: how many base units per 1 foreign unit
  const getRate = (foreignCode) => {
    if (!foreignCode || foreignCode === baseCode || !fxRates[foreignCode] || !fxRates[baseCode]) return 1;
    return fxRates[baseCode] / fxRates[foreignCode];
  };

  const CURRENCIES = Object.keys(fxRates).length > 0 ? Object.keys(fxRates).sort() : [baseCode];

  // Invoice/order currency (fc = foreign currency)
  const fc         = form.invoice_currency || baseCode;
  const fxRate     = parseFloat(form.fx_rate) || getRate(fc);
  const isForeign  = fc !== baseCode;
  const INPUT = (label, field, type = 'text', ph = '') => (
    <div>
      <label className="text-xs font-medium text-gray-600 mb-1 block">{label}</label>
      <input type={type} value={form[field] ?? ''} onChange={e => F(field, e.target.value)}
        className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-transparent" placeholder={ph} />
    </div>
  );

  const SELECT = (label, field, options) => (
    <div>
      <label className="text-xs font-medium text-gray-600 mb-1 block">{label}</label>
      <select value={form[field] || ''} onChange={e => F(field, e.target.value)}
        className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400">
        {options.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
      </select>
    </div>
  );

  const addOrderItem = (p) => {
    if (!p) return;
    const existing = orderItems.find(i => i.product_id === p.product_id);
    if (existing) setOrderItems(orderItems.map(i => i.product_id === p.product_id ? { ...i, quantity: i.quantity + 1 } : i));
    else setOrderItems([...orderItems, { product_id: p.product_id, sku: p.sku, name: p.name, quantity: 1, price: p.price }]);
  };

  const addPurchaseItem = (p) => {
    if (!p) return;
    if (!purchaseItems.find(i => i.product_id === p.product_id))
      setPurchaseItems([...purchaseItems, { product_id: p.product_id, sku: p.sku, name: p.name, quantity: 1, unit_cost: p.cost || 0 }]);
  };

  const titles = {
    product: editItem ? 'Edit Product' : 'Add Product',
    order: 'Create Order',
    warehouse: editItem ? 'Edit Warehouse' : 'Add Warehouse',
    customer: editItem ? 'Edit Customer' : 'Add Customer',
    user: editItem ? 'Edit User' : 'Add User',
    supplier: editItem ? 'Edit Supplier' : 'Add Supplier',
    purchase: 'Receive New Stock',
  };

  const handleSave = () => {
    if (modal === 'product') saveProduct();
    else if (modal === 'order') saveOrder();
    else if (modal === 'warehouse') saveWarehouse();
    else if (modal === 'customer') saveCustomer();
    else if (modal === 'user') saveUser();
    else if (modal === 'supplier') saveSupplier();
    else if (modal === 'purchase') savePurchase();
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={e => { if (e.target === e.currentTarget) close(); }}>
      <div className={`bg-white rounded-2xl w-full ${['order', 'purchase'].includes(modal) ? 'max-w-2xl' : 'max-w-lg'} max-h-[90vh] overflow-y-auto shadow-2xl`}>
        <div className="flex items-center justify-between px-6 py-4 border-b sticky top-0 bg-white rounded-t-2xl z-10">
          <h3 className="font-semibold text-lg text-gray-900">{titles[modal]}</h3>
          <button onClick={close} className="p-1 rounded-lg hover:bg-gray-100"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-6 space-y-4">

          {modal === 'product' && <>
            {INPUT('Product Name *', 'name', 'text', 'iPhone 11 64GB Black')}
            <div className="grid grid-cols-2 gap-3">{INPUT('Brand', 'brand', 'text', 'Apple')}{INPUT('Category', 'category', 'text', 'Smartphones')}</div>
            <div className="grid grid-cols-2 gap-3">{INPUT('SKU', 'sku', 'text', 'Auto-generated')}{INPUT('Barcode', 'barcode', 'text', 'Optional')}</div>
            <div className="grid grid-cols-2 gap-3">
              {INPUT('Selling Price (S$)', 'price', 'number')}
              <div>
                <label className="text-xs font-medium text-gray-600 mb-1 block">Cost (S$)</label>
                <div className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm bg-gray-50 text-gray-500">
                  {editItem ? `S$${editItem.cost || 0} (auto-calculated from received stock)` : 'Auto-calculated when stock is received'}
                </div>
              </div>
            </div>
            <div className="grid grid-cols-3 gap-3">{INPUT('Initial Stock', 'stock', 'number')}{INPUT('Min Stock Alert', 'min_stock', 'number')}{INPUT('Weight (kg)', 'weight', 'number')}</div>
            <div className="grid grid-cols-2 gap-3">
              {INPUT('Dimensions', 'dimensions', 'text', 'L x W x H')}
              {SELECT('Warehouse', 'warehouse_id', [['', 'Select...'], ...warehouses.map(w => [w.warehouse_id, w.name])])}
            </div>
            <div className="grid grid-cols-2 gap-3">{INPUT('Shopee ID', 'shopee_id', 'text', 'Optional')}{INPUT('Image URL', 'image_url', 'text', 'https://...')}</div>
          </>}

          {modal === 'order' && <>
            <div>
              <label className="text-xs font-medium text-gray-600 mb-1 block">Select Existing Customer (or type new below)</label>
              <select onChange={e => {
                const c = customers.find(x => x.customer_id === e.target.value);
                if (c) setForm(prev => ({ ...prev, customer_id: c.customer_id, customer_name: c.name, customer_email: c.email, customer_phone: c.phone, shipping_address: c.address, shipping_city: c.city || 'Singapore', shipping_postal: c.postal_code }));
              }} className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400">
                <option value="">-- New Customer --</option>
                {customers.map(c => <option key={c.customer_id} value={c.customer_id}>{c.name} ({c.email})</option>)}
              </select>
            </div>
            <div className="grid grid-cols-2 gap-3">{INPUT('Customer Name *', 'customer_name', 'text', 'John Tan')}{INPUT('Email', 'customer_email', 'text', 'john@email.com')}</div>
            <div className="grid grid-cols-2 gap-3">{INPUT('Phone', 'customer_phone', 'text', '+65 9123 4567')}{SELECT('Marketplace', 'marketplace', [['shopee', 'Shopee'], ['carousell', 'Carousell'], ['ebay', 'eBay']])}</div>
            {INPUT('Shipping Address', 'shipping_address', 'text', 'Blk 123 Toa Payoh')}
            <div className="grid grid-cols-2 gap-3">{INPUT('City', 'shipping_city', 'text', 'Singapore')}{INPUT('Postal Code', 'shipping_postal', 'text', '310123')}</div>

            {/* Order Currency */}
            <div className="flex items-end gap-3 p-3 bg-orange-50 rounded-xl border border-orange-100">
              <div className="flex-1">
                <label className="text-xs font-medium text-gray-600 mb-1 block">Order Currency</label>
                <select value={form.invoice_currency || baseCode} onChange={e => { F('invoice_currency', e.target.value); F('fx_rate', getRate(e.target.value).toFixed(4)); }}
                  className="w-full border rounded-lg px-3 py-2 text-sm bg-white">
                  {CURRENCIES.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              {isForeign && <>
                <div>
                  <label className="text-xs font-medium text-gray-600 mb-1 block">Rate (1 {fc} = ?{baseCode})</label>
                  <input type="number" step="0.0001" value={form.fx_rate || fxRate.toFixed(4)}
                    onChange={e => F('fx_rate', e.target.value)}
                    className="w-28 border rounded-lg px-3 py-2 text-sm bg-white" />
                </div>
                <div className="text-xs text-orange-700 pb-2">
                  Revenue tracked in {baseCode}<br />
                  <span className="text-orange-400">{fxSource ? `via ${fxSource}` : 'custom rate'}</span>
                </div>
              </>}
              {!isForeign && <p className="text-xs text-gray-400 pb-2">Invoicing in {baseCode} — your base currency (no conversion needed)</p>}
            </div>
            <div>
              <label className="text-xs font-medium text-gray-600 mb-1 block">Add Items</label>
              <select onChange={e => { const p = products.find(x => x.product_id === e.target.value); if (p) addOrderItem(p); e.target.value = ''; }} className="w-full border rounded-lg px-3 py-2 text-sm">
                <option value="">Select product to add...</option>
                {products.filter(p => p.stock > 0).map(p => <option key={p.product_id} value={p.product_id}>{p.name} — S${p.price} (Stock: {p.stock})</option>)}
              </select>
            </div>
            {orderItems.length > 0 && (
              <div className="border rounded-lg overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50"><tr><Th>Item</Th><Th>Price</Th><Th>Qty</Th><Th>Avail</Th><Th>Total</Th><Th></Th></tr></thead>
                  <tbody className="divide-y">
                    {orderItems.map((item, idx) => {
                      const prod = products.find(p => p.product_id === item.product_id);
                      const avail = prod?.stock || 0;
                      const over = item.quantity > avail;
                      return (
                        <tr key={idx} className={over ? 'bg-red-50' : ''}>
                          <Td className="font-medium text-xs">{item.name}</Td><Td>S${item.price}</Td>
                          <Td><input type="number" min="1" max={avail} value={item.quantity} onChange={e => setOrderItems(orderItems.map((it, i) => i === idx ? { ...it, quantity: Math.min(parseInt(e.target.value) || 1, avail) } : it))} className={`w-16 border rounded px-2 py-1 text-sm ${over ? 'border-red-400' : ''}`} /></Td>
                          <Td className={`text-xs ${over ? 'text-red-600 font-bold' : 'text-gray-400'}`}>{avail}</Td>
                          <Td className="font-semibold">S${(item.price * item.quantity).toFixed(2)}</Td>
                          <Td><button onClick={() => setOrderItems(orderItems.filter((_, i) => i !== idx))} className="text-red-400 hover:text-red-600"><X className="w-4 h-4" /></button></Td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
                <div className="bg-gray-50 px-4 py-2 flex justify-between font-semibold text-sm"><span>Subtotal</span><span>S${orderTotal.toFixed(2)}</span></div>
              </div>
            )}
            <div className="grid grid-cols-3 gap-3">
              {INPUT(`Shipping Fee (${cur})`, 'shipping_fee', 'number')}
              {INPUT(`Discount (${cur})`, 'discount', 'number')}
              <div>
                <label className="text-xs font-medium text-gray-600 mb-1 block">GST / Tax</label>
                <select value={form.gst_rate ?? '0'} onChange={e => F('gst_rate', e.target.value)}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400">
                  {taxRates.map(t => (
                    <option key={t.id} value={String(t.rate)}>{t.name}</option>
                  ))}
                </select>
              </div>
            </div>
            {orderItems.length > 0 && (() => {
              const shipping  = parseFloat(form.shipping_fee) || 0;
              const discount  = parseFloat(form.discount) || 0;
              const gstRate   = parseFloat(form.gst_rate) || 0;
              const beforeTax = orderTotal + shipping - discount;
              const gstAmt    = parseFloat((beforeTax * gstRate / 100).toFixed(2));
              const grandTotalFc = parseFloat((beforeTax + gstAmt).toFixed(2));
              const grandTotalBase = isForeign ? parseFloat((grandTotalFc * fxRate).toFixed(2)) : grandTotalFc;
              return (
                <div className="border rounded-xl overflow-hidden">
                  <div className="divide-y text-sm">
                    {isForeign && (
                      <div className="flex justify-between px-4 py-2 bg-orange-50 text-xs text-orange-700">
                        <span>Order in {fc}</span>
                        <span>Rate: 1 {fc} = {cur}{fxRate.toFixed(4)}</span>
                      </div>
                    )}
                    <div className="flex justify-between px-4 py-2 bg-gray-50"><span className="text-gray-500">Subtotal</span><span>{fc !== baseCode ? fc : ''} {orderTotal.toFixed(2)}</span></div>
                    {shipping > 0 && <div className="flex justify-between px-4 py-2"><span className="text-gray-500">Shipping</span><span>+ {shipping.toFixed(2)}</span></div>}
                    {discount > 0 && <div className="flex justify-between px-4 py-2"><span className="text-gray-500">Discount</span><span className="text-green-600">- {discount.toFixed(2)}</span></div>}
                    {gstAmt > 0 && <div className="flex justify-between px-4 py-2"><span className="text-gray-500">Tax ({gstRate}%)</span><span>+ {gstAmt.toFixed(2)}</span></div>}
                    {isForeign && (
                      <div className="flex justify-between px-4 py-2 text-xs text-blue-700 bg-blue-50">
                        <span>Converted to {baseCode}</span>
                        <span>{fc} {grandTotalFc.toFixed(2)} × {fxRate.toFixed(4)} = {cur}{grandTotalBase.toFixed(2)}</span>
                      </div>
                    )}
                  </div>
                  <div className="flex justify-between items-center px-4 py-3 bg-blue-50">
                    <span className="font-bold text-blue-800">Grand Total ({baseCode})</span>
                    <span className="text-xl font-bold text-blue-800">{cur}{grandTotalBase.toFixed(2)}</span>
                  </div>
                </div>
              );
            })()}
            {INPUT('Notes', 'notes', 'text', 'Optional order notes')}
          </>}

          {modal === 'warehouse' && <>
            {INPUT('Warehouse Name *', 'name', 'text', 'Singapore Main Hub')}
            {INPUT('Location', 'location', 'text', '10 Changi Business Park')}
            <div className="grid grid-cols-2 gap-3">{INPUT('Manager', 'manager', 'text', 'James Tan')}{INPUT('Phone', 'phone', 'text', '+65 6234 5678')}</div>
            {INPUT('Capacity (units)', 'capacity', 'number')}
          </>}

          {modal === 'customer' && <>
            {INPUT('Customer Name *', 'name', 'text', 'John Tan')}
            <div className="grid grid-cols-2 gap-3">{INPUT('Email', 'email', 'text', 'john@email.com')}{INPUT('Phone', 'phone', 'text', '+65 9123 4567')}</div>
            {INPUT('Address', 'address', 'text', 'Blk 123 Toa Payoh Lorong 1')}
            <div className="grid grid-cols-2 gap-3">{INPUT('City', 'city', 'text', 'Singapore')}{INPUT('Postal Code', 'postal_code', 'text', '310123')}</div>
            {INPUT('Notes', 'notes', 'text', 'VIP customer, prefer express delivery')}
          </>}

          {modal === 'user' && <>
            {INPUT('Full Name *', 'name', 'text', 'James Tan')}
            {INPUT('Email *', 'email', 'text', 'james@agora-sg')}
            {!editItem && INPUT('Password', 'password', 'text', 'Default: password123')}
            <div className="grid grid-cols-2 gap-3">
              {SELECT('Role', 'role', [['staff', 'Staff'], ['manager', 'Manager'], ['admin', 'Admin']])}
              {INPUT('Department', 'department', 'text', 'Operations')}
            </div>
            {INPUT('Phone', 'phone', 'text', '+65 9123 4567')}
          </>}

          {modal === 'supplier' && <>
            <div className="grid grid-cols-2 gap-3">{INPUT('Contact Name *', 'name', 'text', 'James Tan')}{INPUT('Company *', 'company', 'text', 'Apple Singapore Pte Ltd')}</div>
            <div className="grid grid-cols-2 gap-3">{INPUT('Email', 'email', 'text', 'james@apple.sg')}{INPUT('Phone', 'phone', 'text', '+65 6234 5678')}</div>
            {INPUT('Address', 'address', 'text', '10 Changi Business Park')}
            <div className="grid grid-cols-3 gap-3">{INPUT('City', 'city', 'text', 'Singapore')}{INPUT('Country', 'country', 'text', 'Singapore')}{INPUT('Postal Code', 'postal_code', 'text', '486029')}</div>
            <div className="grid grid-cols-2 gap-3">
              {SELECT('Payment Terms', 'payment_terms', [['Net 30', 'Net 30'], ['Net 60', 'Net 60'], ['Net 15', 'Net 15'], ['COD', 'Cash on Delivery'], ['Prepaid', 'Prepaid']])}
              {INPUT('Lead Time (days)', 'lead_time_days', 'number')}
            </div>
            {INPUT('Notes', 'notes', 'text', 'Preferred supplier for Apple products')}
          </>}

          {modal === 'purchase' && <>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-medium text-gray-600 mb-1 block">Supplier {addedProductIds.length > 0 && compatibleSupplierIds.length < suppliers.length && <span className="text-blue-500">({compatibleSupplierIds.length} compatible)</span>}</label>
                <select value={form.supplier || ''} onChange={e => F('supplier', e.target.value)}
                  className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400">
                  <option value="">Select supplier...</option>
                  {addedProductIds.length > 0 && compatibleSupplierIds.length > 0 && (
                    <optgroup label="Carries all selected products">
                      {suppliers.filter(s => compatibleSupplierSet.has(s.supplier_id)).map(s => (
                        <option key={s.supplier_id} value={s.name}>{s.name} ({s.company})</option>
                      ))}
                    </optgroup>
                  )}
                  {addedProductIds.length > 0 && suppliers.filter(s => !compatibleSupplierSet.has(s.supplier_id)).length > 0 && (
                    <optgroup label="Other suppliers">
                      {suppliers.filter(s => !compatibleSupplierSet.has(s.supplier_id)).map(s => (
                        <option key={s.supplier_id} value={s.name} disabled>{s.name} ({s.company})</option>
                      ))}
                    </optgroup>
                  )}
                  {addedProductIds.length === 0 && suppliers.map(s => (
                    <option key={s.supplier_id} value={s.name}>{s.name} ({s.company})</option>
                  ))}
                </select>
              </div>
              {INPUT('Reference / Invoice #', 'reference', 'text', 'INV-2026-0321')}
            </div>
            {SELECT('Warehouse', 'warehouse_id', [['', 'Select...'], ...warehouses.map(w => [w.warehouse_id, w.name])])}

            {/* Invoice Currency selector */}
            <div className="flex items-end gap-3 p-3 bg-blue-50 rounded-xl border border-blue-100">
              <div className="flex-1">
                <label className="text-xs font-medium text-gray-600 mb-1 block">Invoice Currency</label>
                <select value={fc} onChange={e => { F('invoice_currency', e.target.value); F('fx_rate', getRate(e.target.value).toFixed(4)); }}
                  className="w-full border rounded-lg px-3 py-2 text-sm bg-white">
                  {CURRENCIES.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              {isForeign && <>
                <div>
                  <label className="text-xs font-medium text-gray-600 mb-1 block">Rate (1 {fc} = ?{baseCode})</label>
                  <input type="number" step="0.0001" value={form.fx_rate || fxRate.toFixed(4)}
                    onChange={e => F('fx_rate', e.target.value)}
                    className="w-28 border rounded-lg px-3 py-2 text-sm bg-white" />
                </div>
                <div className="text-xs text-blue-700 pb-2">
                  1 {fc} = {cur}{fxRate.toFixed(4)}<br />
                  <span className="text-blue-400">{fxSource ? `via ${fxSource}` : 'custom'}</span>
                </div>
              </>}
              {!isForeign && <p className="text-xs text-gray-400 pb-2">Invoicing in {baseCode} — your base currency (no conversion needed)</p>}
            </div>

            <div>
              <label className="text-xs font-medium text-gray-600 mb-1 block">Add Products to Receive {selectedSupplier && supplierProducts.length > 0 && <span className="text-blue-500">({supplierProducts.length} products from {selectedSupplier.name})</span>}</label>
              <select onChange={e => { const val = e.target.value; const p = products.find(x => x.product_id === val); if (p) { const sp = allSupplierProducts.find(sp => sp.product_id === p.product_id && selectedSupplier && sp.supplier_id === selectedSupplier.supplier_id); addPurchaseItem(sp?.unit_cost ? { ...p, unit_cost: sp.unit_cost } : p); e.target.value = ''; } }}
                className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400">
                <option value="">{selectedSupplier ? `Select product from ${selectedSupplier.name}...` : 'Select any product...'}</option>
                {availableProducts.filter(p => !addedProductIds.includes(p.product_id)).map(p => {
                  const sp = allSupplierProducts.find(sp => sp.product_id === p.product_id && selectedSupplier && sp.supplier_id === selectedSupplier.supplier_id);
                  return <option key={p.product_id} value={p.product_id}>{p.name} (Stock: {p.stock}{sp ? `, S$${sp.unit_cost}` : ''})</option>;
                })}
              </select>
            </div>
            {purchaseItems.length > 0 && (
              <div className="border rounded-lg overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50">
                    <tr>
                      <Th>Product</Th><Th>Curr. Stock</Th><Th>Qty</Th>
                      <Th>Unit Cost {isForeign ? `(${fc})` : `(${baseCode})`}</Th>
                      {isForeign && <Th>= {baseCode}</Th>}
                      <Th>Line Total ({baseCode})</Th><Th></Th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {purchaseItems.map((item, idx) => {
                      const prod = products.find(p => p.product_id === item.product_id);
                      const unitBase = isForeign ? item.unit_cost * fxRate : item.unit_cost;
                      const lineBase = unitBase * item.quantity;
                      return (
                        <tr key={idx}>
                          <Td className="font-medium text-xs">{item.name}</Td>
                          <Td className="text-gray-400">{prod?.stock || 0}</Td>
                          <Td><input type="number" min="1" value={item.quantity} onChange={e => setPurchaseItems(purchaseItems.map((it, i) => i === idx ? { ...it, quantity: parseInt(e.target.value) || 1 } : it))} className="w-16 border rounded px-2 py-1 text-sm" /></Td>
                          <Td><input type="number" min="0" step="0.01" value={item.unit_cost} onChange={e => setPurchaseItems(purchaseItems.map((it, i) => i === idx ? { ...it, unit_cost: parseFloat(e.target.value) || 0 } : it))} className="w-24 border rounded px-2 py-1 text-sm" /></Td>
                          {isForeign && <Td className="text-blue-700 text-xs">{cur}{unitBase.toFixed(2)}</Td>}
                          <Td className="font-semibold">{cur}{lineBase.toFixed(2)}</Td>
                          <Td><button onClick={() => setPurchaseItems(purchaseItems.filter((_, i) => i !== idx))} className="text-red-400 hover:text-red-600"><X className="w-4 h-4" /></button></Td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}

            {/* Extra charges */}
            <div className="grid grid-cols-3 gap-3">
              {INPUT(`Freight / Shipping (${cur})`, 'freight_cost', 'number')}
              {INPUT(`Supplier Discount (${cur})`, 'supplier_discount', 'number')}
              <div>
                <label className="text-xs font-medium text-gray-600 mb-1 block">Input Tax / GST</label>
                <select value={form.gst_rate ?? '0'} onChange={e => F('gst_rate', e.target.value)}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400">
                  {taxRates.map(t => (
                    <option key={t.id} value={String(t.rate)}>{t.name}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* Cost breakdown */}
            {(() => {
              const subtotalBase = isForeign
                ? purchaseItems.reduce((s, i) => s + i.unit_cost * i.quantity * fxRate, 0)
                : purchaseTotal;
              const freight   = parseFloat(form.freight_cost) || 0;
              const discount  = parseFloat(form.supplier_discount) || 0;
              const gstRate   = parseFloat(form.gst_rate) || 0;
              const beforeGst = subtotalBase + freight - discount;
              const gstAmt    = parseFloat((beforeGst * gstRate / 100).toFixed(2));
              const grandTotal = parseFloat((beforeGst + gstAmt).toFixed(2));
              return (
                <div className="border rounded-xl overflow-hidden">
                  <div className="divide-y text-sm">
                    {isForeign && (
                      <div className="flex justify-between px-4 py-2 bg-blue-50 text-xs text-blue-700">
                        <span>Products subtotal ({fc})</span>
                        <span>{fc} {purchaseTotal.toFixed(2)} × {fxRate.toFixed(4)} = {cur}{subtotalBase.toFixed(2)}</span>
                      </div>
                    )}
                    <div className="flex justify-between px-4 py-2 bg-gray-50"><span className="text-gray-500">Products subtotal ({baseCode})</span><span>{cur}{subtotalBase.toFixed(2)}</span></div>
                    {freight > 0 && <div className="flex justify-between px-4 py-2"><span className="text-gray-500">Freight / Shipping</span><span>+ {cur}{freight.toFixed(2)}</span></div>}
                    {discount > 0 && <div className="flex justify-between px-4 py-2"><span className="text-gray-500">Supplier Discount</span><span className="text-green-600">- {cur}{discount.toFixed(2)}</span></div>}
                    {gstAmt > 0 && <div className="flex justify-between px-4 py-2"><span className="text-gray-500">Input Tax ({gstRate}%)</span><span>+ {cur}{gstAmt.toFixed(2)}</span></div>}
                  </div>
                  <div className="flex justify-between items-center px-4 py-3 bg-emerald-50">
                    <span className="font-bold text-emerald-800">Total Purchase Cost ({baseCode})</span>
                    <span className="text-xl font-bold text-emerald-700">{cur}{grandTotal.toFixed(2)}</span>
                  </div>
                </div>
              );
            })()}

            {INPUT('Notes', 'notes', 'text', 'Batch from supplier, delivery ref #123')}
          </>}

        </div>
        <div className="flex justify-end gap-3 px-6 py-4 border-t sticky bottom-0 bg-white rounded-b-2xl">
          <button onClick={close} className="px-4 py-2 text-sm text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50">Cancel</button>
          <button onClick={handleSave} className="px-6 py-2 text-sm bg-blue-500 text-white rounded-lg hover:bg-blue-600 font-medium shadow-sm">
            {editItem ? 'Update' : 'Create'}
          </button>
        </div>
      </div>
    </div>
  );
}
